import { action, computed, observable } from 'mobx';

import { BrowserStorage, IAppConfig } from '@estee/elc-universal-utils';

import { SocialSignUpType } from '~api/RampPgApiSdk';
import {
    brandIdValPath,
    IamType,
    IamTypeKey,
    rampCommonPath,
    regionIdValPath,
    StorageKey
} from '~constants/Constants';
import { IJourney, Journeys, JourneyType } from '~constants/Journeys';
import { SignInType } from '~constants/SignInTypeEnum';
import {
    Config,
    ICheckableItem,
    ICheckboxGroup,
    IDataConfig,
    IFieldsValidations,
    IFormConfig,
    IFormSchema,
    InputSchemaType,
    IOtpConfig,
    IRampPreloadedConfig,
    isCheckboxGroup,
    isFieldset,
    ISocialButtonWrapperConfig,
    ISocialProvider,
    isPasswordCriteria,
    isPasswordStrength,
    UserIdType
} from '~domain/entities/Config';
import { IUserFormValues } from '~domain/entities/UserFormStore';
import { ACCOUNT_HOME_URL } from '~routes/routes';
import { FieldValidations } from '~utils/FieldValidations';

export class ConfigRepository {
    @observable public config: Config;
    @observable public configNodeName: string;
    @observable public redirectUrlOverride: string;
    @observable public socialRedirectUrl: string;
    @observable public isSocialProfile: boolean = false;
    // string to record the social provider name for social profile form
    @observable public socialProvider: string = '';
    @observable public formerSelectedProvider: string = '';
    @observable public isConfigLoaded: boolean = false;
    // flag for user coming from path 'guest-checkout' and should show 'guest' and 'sign in' tabs
    @observable public isGuestAndSignIn: boolean = false;
    // flag for redirect url contains the '/checkout/'
    @observable public isCheckoutFlow: boolean = false;
    @observable public isSetupReady: boolean = false;
    @observable public fieldsValidations: Record<string, FieldValidations> = {};
    @observable public iamType: IamType;
    @observable public readonlyFields: Set<string> = new Set([]);
    @observable public highlightedFields: Set<string> = new Set([]);
    @observable public socialSignUpType: SocialSignUpType;
    @observable public hasLoyalty: boolean;

    constructor(config: IAppConfig) {
        this.config = new Config(config);
    }

    @action
    public onSetupReady = () => {
        this.isSetupReady = true;
    };

    @action
    public setSocialSignUpType = (socialSignUp: string) => {
        this.socialSignUpType = socialSignUp as SocialSignUpType;
    };

    @action
    public setSocialProvider = (provider: string) => {
        this.socialProvider = provider;
    };

    @action
    public setFormerSelectedProvider = (provider: string) => {
        this.formerSelectedProvider = provider;
    };

    @action
    public setIsSocialProfile = (isSocialProfile: boolean) => {
        this.isSocialProfile = isSocialProfile;
    };

    @action
    public setIsGuestAndSignIn = (isGuestAndSignIn: boolean) => {
        this.isGuestAndSignIn = isGuestAndSignIn;
    };

    @action
    public setIsCheckoutFlow = (isCheckoutFlow: boolean) => {
        this.isCheckoutFlow = isCheckoutFlow;
    };

    @computed
    public get rampCommon() {
        return this.config.config[rampCommonPath];
    }

    // all the social providers configured in BUID
    @computed
    public get socialProviders(): ISocialButtonWrapperConfig | undefined {
        return this.rampCommon?.socialProviders;
    }

    public getSocialSignUpProvider(provider: SocialSignUpType) {
        return this.rampCommon?.socialSignUpProviders?.providers.find(
            (c) => c.providerName === provider
        );
    }

    public getSocialProviderByName = (name: SignInType): ISocialProvider | undefined => {
        return this.socialProviders?.providers?.find(({ providerName }) => providerName === name);
    };

    public get otpConfig(): IOtpConfig | undefined {
        return this.rampCommon?.otp;
    }

    public get badCharValidation(): string | undefined {
        return this.rampCommon?.badCharValidation;
    }

    public get dataConfig(): IDataConfig {
        const { siteType, facebookAppId } = this.rampCommon || {};

        return {
            showLoginForm: siteType === 'ecommerce',
            facebookAppId
        };
    }

    @action
    public loadRampConfigs = (configNodeName: string) => {
        const [firstKey, secondKey] = configNodeName.split('.');
        this.configNodeName = secondKey;
        const iamConfig = this.config?.config?.[firstKey]?.[secondKey];

        if (iamConfig) {
            this.config.updateConfig({ iamConfig });
            this.isConfigLoaded = true;
        }
    };

    @action
    public setConfigs = (config: Partial<IRampPreloadedConfig>) => {
        this.addBaseConfigs(config);
        this.config.updateConfig(config);
    };

    @action
    public setIamType = (path: string) => {
        const [type] = path.split('/').reverse();
        const indexOfType = Object.values(IamType).indexOf(type as IamType);
        this.iamType = IamType[Object.keys(IamType)[indexOfType] as IamTypeKey];
    };

    @action
    public setHasLoyalty = (hasLoyalty: boolean) => {
        this.hasLoyalty = hasLoyalty;
    };

    private addBaseConfigs = (config: Partial<IRampPreloadedConfig>) => {
        const paths = [brandIdValPath, regionIdValPath, rampCommonPath];

        paths.forEach((path) => {
            const [firstKey, secondKey] = path.split('.');
            config[path] = config?.[firstKey]?.[secondKey];
        });
    };

    // TODO: refactor to avoid repeated sections reduce processing (APACFTRS1-770)
    @computed
    public get signUpFormInitialValues(): IUserFormValues {
        const { schema: { sections = [] } = {} } = this.iamConfig || {};
        if (!sections.length) {
            return {};
        } else {
            return sections.reduce((values, section) => {
                return section.items.reduce((sectionValues, item) => {
                    if (isCheckboxGroup(item)) {
                        let initialValues = sectionValues;
                        const { items, selectAll } = item;
                        if (selectAll) {
                            const itemValues = new Set(items.map((n) => n.initialValue));
                            // Make sure select all checked status consist with sub-items checked status
                            const selectAllValue =
                                itemValues.size === 1 ? !!Array.from(itemValues)[0] : false;
                            initialValues = {
                                ...initialValues,
                                [selectAll.name]: selectAllValue
                            };
                        }

                        return item.items.reduce(
                            (checkboxGroupValues, checkbox) => ({
                                ...checkboxGroupValues,
                                [checkbox.name]: checkbox.initialValue || false
                            }),
                            { ...initialValues }
                        );
                    }
                    if (isFieldset(item)) {
                        return item.items.reduce(
                            (fieldsetValues, fieldsetItem) => ({
                                ...fieldsetValues,
                                [fieldsetItem.name]: fieldsetItem.initialValue
                            }),
                            { ...sectionValues }
                        );
                    } else if (isPasswordCriteria(item) || isPasswordStrength(item)) {
                        return sectionValues;
                    } else {
                        return item.initialValue === undefined
                            ? sectionValues
                            : { ...sectionValues, [item.name]: item.initialValue };
                    }
                }, values);
            }, {});
        }
    }

    // This data is determined when RAMP configurations are set, and will not be updated. Returns a collection of checkbox field names whose checked value should be inverse.
    // Note: Not used in Reactive Context so setting this as computed does not take advantage of caching.
    private _inverseFields: string[];
    public get inverseFields() {
        if (this._inverseFields) {
            return this._inverseFields;
        }
        if (!this.iamConfig?.schema) {
            return [];
        }
        const {
            schema: { sections }
        } = this.iamConfig;
        this._inverseFields = [];
        sections.forEach((section) => {
            section.items.forEach((item) => {
                const { name, inputType } = item;
                if (inputType === 'checkbox') {
                    const { inverseCheckedValue } = item as ICheckableItem;
                    inverseCheckedValue && this._inverseFields.push(name);
                }
                if (inputType === 'checkboxGroup' || inputType === 'accordionCheckboxGroup') {
                    const { items, inverseCheckedValue } = item as ICheckboxGroup;
                    inverseCheckedValue &&
                        items.forEach((groupItem) => {
                            this._inverseFields.push(groupItem.name);
                        });
                }
            });
        });

        return this._inverseFields;
    }

    private updateDerivedPutRequestFields = (config: IFormConfig) => {
        if (!config?.putRequestFields) {
            return config;
        }

        const { putRequestFields, postRequestFields } = config;

        const modifications = putRequestFields.filter((field) => /^[+-]/.test(field));

        if (!modifications.length) {
            return config;
        } else {
            const fields = modifications.reduce(
                (arr, modification) => {
                    const operator = modification[0];
                    const field = modification.substring(1);
                    if (arr.includes(field) && operator === '-') {
                        arr.splice(arr.indexOf(field), 1);
                    }
                    if (!arr.includes(field) && operator === '+') {
                        arr.push(field);
                    }

                    return arr;
                },
                [...postRequestFields]
            );

            return {
                ...config,
                putRequestFields: fields
            };
        }
    };

    @computed public get iamConfig() {
        const { iamConfig } = this.config.config;

        return this.updateDerivedPutRequestFields(iamConfig) as IFormConfig;
    }

    // For mobile only registration, there could be only `mobileNumber` in identifiers
    @computed public get emailIdentifierFieldName() {
        const { identifiers = [] } = this.iamConfig || {};
        if (identifiers.includes(UserIdType.PC_EMAIL_ADDRESS)) {
            return UserIdType.PC_EMAIL_ADDRESS;
        } else if (identifiers.includes(UserIdType.EMAIL_ADDRESS)) {
            return UserIdType.EMAIL_ADDRESS;
        } else {
            return undefined;
        }
    }

    @computed public get userInfoDisplay() {
        return this.iamConfig?.userInfoDisplay || { name: '', email: '' };
    }

    @computed public get formSchema() {
        return this.iamConfig?.schema;
    }

    @computed public get flatFormFields(): InputSchemaType[] {
        if (!this.formSchema) {
            return [];
        }

        return this.formSchema.sections.reduce((items: InputSchemaType[], section) => {
            return items.concat(section.items);
        }, []);
    }

    public getFieldConfig = (name: string) =>
        this.flatFormFields.find((field) => field.name === name);

    @action public setFieldsValidations(fieldsValidations: Record<string, FieldValidations>) {
        this.fieldsValidations = fieldsValidations;
    }

    @action
    public setReadonlyField = (fieldName: string) => this.readonlyFields.add(fieldName);

    @action
    public setHighlightedField = (fieldName: string) => this.highlightedFields.add(fieldName);

    @action
    public resetHighlightedFields = () => (this.highlightedFields = new Set());

    @action
    public removeHighlight = (fieldName: string) => this.highlightedFields.delete(fieldName);

    @computed
    public get journey(): IJourney {
        return Journeys[(this.iamConfig?.submitJourney as JourneyType) ?? JourneyType.IN_STORE];
    }

    @action
    public setRedirectUrlOverride = (redirectUrl: string) => {
        this.redirectUrlOverride = redirectUrl;
    };

    public get onSuccessRedirectUrl(): string {
        const redirectUrl = BrowserStorage.getItem(StorageKey.ACCOUNT_REDIRECT_URL, 'localStorage');

        return (
            this.redirectUrlOverride || redirectUrl || this.journey.redirectUrl || ACCOUNT_HOME_URL
        );
    }

    @action
    public setSocialRedirectUrl = (socialRedirectUrl: string) => {
        this.socialRedirectUrl = socialRedirectUrl;
    };

    public getFormInitialValues({ configName, defaultValues }: IIntitialValues) {
        const items = this.config.config?.ramp?.[configName]?.schema?.sections?.[0]?.items;

        return (
            items?.reduce((values: IValues, item: IItem) => {
                const { name, initialValue } = item;

                if (typeof initialValue !== 'undefined' && initialValue !== null) {
                    values[name] = initialValue;
                }

                return values;
            }, defaultValues) ?? defaultValues
        );
    }

    public get passwordFields(): IFormSchema {
        const items = this.config.config?.ramp?.signUp?.schema?.sections?.[0]?.items;
        const passwordNamesFields = new Set(['password', 'passwordStrength', 'passwordCriteria']);
        const passwordItems = items?.filter(({ name }: IItem) => passwordNamesFields.has(name));

        return { sections: [{ name: 'resetPassword', items: passwordItems }] };
    }

    public get passwordValidations(): IFieldsValidations {
        const password = this.config.config?.ramp?.signUp?.fieldsValidations?.password;

        return { password };
    }

    public get excludedFromSocialLogin() {
        return this.socialProviders?.excludedUseCases?.some((useCase) => useCase === this.iamType);
    }
}

type IValue = boolean | number | string | undefined | null;

interface IValues {
    [key: string]: IValue;
}

interface IItem {
    name: string;
    initialValue?: IValue;
}

interface IIntitialValues {
    configName: string;
    defaultValues: IValues;
}
